import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
    AppBar,
    Box,
    CssBaseline,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Typography,
    Menu,
    MenuItem,
    Avatar,
    Divider,
    useTheme,
    alpha,
    Badge,
    Tooltip,
    Container,
} from '@mui/material';
import {
    Menu as MenuIcon,
    Dashboard as DashboardIcon,
    Assignment as AssignmentIcon,
    BarChart as BarChartIcon,
    Settings as SettingsIcon,
    Email as EmailIcon,
    Logout,
    Brightness4,
    Brightness7,
    Notifications,
    Circle,
    WebAsset,
    CloudQueue,
    Code,
} from '@mui/icons-material';
import { observer } from 'mobx-react-lite';
import { useStore } from '../store';

const drawerWidth = 280;

interface MenuItem {
    text: string;
    icon: React.ReactElement;
    path: string;
    adminOnly?: boolean;
    badge?: number;
}

const Layout: React.FC = observer(() => {
    const { authStore } = useStore();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [darkMode, setDarkMode] = useState(false);

    const menuItems: MenuItem[] = [
        {
            text: 'Панель управления',
            icon: <DashboardIcon />,
            path: '/dashboard'
        },
        {
            text: 'Заявки',
            icon: <AssignmentIcon />,
            path: '/applications',
            badge: 5
        },
        {
            text: 'Статистика',
            icon: <BarChartIcon />,
            path: '/statistics'
        },
        {
            text: 'Email шаблоны',
            icon: <EmailIcon />,
            path: '/email-templates'
        },
        {
            text: 'Формы Tilda',
            icon: <WebAsset />,
            path: '/tilda-forms'
        },
        {
            text: 'Развертывания',
            icon: <CloudQueue />,
            path: '/deployments',
            adminOnly: true
        },
        {
            text: 'Шаблоны развертывания',
            icon: <Code />,
            path: '/deployment-templates',
            adminOnly: true
        },
        {
            text: 'Настройки',
            icon: <SettingsIcon />,
            path: '/settings',
            adminOnly: true
        },
    ];

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        authStore.logout();
        navigate('/login');
        handleMenuClose();
    };

    const toggleDarkMode = () => {
        setDarkMode(!darkMode);
    };

    const filteredMenuItems = menuItems.filter(item =>
        !item.adminOnly || authStore.isAdmin
    );

    const getUserInitials = () => {
        const username = authStore.user?.username || 'U';
        return username.charAt(0).toUpperCase();
    };

    const drawer = (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Toolbar
                sx={{
                    py: 3,
                    px: 3,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                }}
            >
                <Box display="flex" alignItems="center" width="100%">
                    <Avatar
                        sx={{
                            bgcolor: 'white',
                            color: 'primary.main',
                            mr: 2,
                            width: 48,
                            height: 48,
                            fontSize: '1.5rem',
                            fontWeight: 700,
                        }}
                    >
                        A
                    </Avatar>
                    <Box>
                        <Typography
                            variant="h6"
                            noWrap
                            sx={{
                                color: 'white',
                                fontWeight: 700,
                            }}
                        >
                            Applications
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: alpha('#ffffff', 0.8),
                            }}
                        >
                            Management System
                        </Typography>
                    </Box>
                </Box>
            </Toolbar>

            <Box sx={{ px: 2, py: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ px: 2, textTransform: 'uppercase', fontWeight: 600 }}>
                    Основное меню
                </Typography>
            </Box>

            <List sx={{ px: 2, flex: 1 }}>
                {filteredMenuItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                        <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                            <ListItemButton
                                selected={isActive}
                                onClick={() => navigate(item.path)}
                                sx={{
                                    borderRadius: 2,
                                    position: 'relative',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                    },
                                    '&.Mui-selected': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                                        '&:hover': {
                                            bgcolor: alpha(theme.palette.primary.main, 0.16),
                                        },
                                        '& .MuiListItemIcon-root': {
                                            color: 'primary.main',
                                        },
                                        '& .MuiListItemText-primary': {
                                            color: 'primary.main',
                                            fontWeight: 600,
                                        },
                                    },
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 40,
                                        color: isActive ? 'primary.main' : 'text.secondary',
                                    }}
                                >
                                    {item.badge ? (
                                        <Badge badgeContent={item.badge} color="error">
                                            {item.icon}
                                        </Badge>
                                    ) : (
                                        item.icon
                                    )}
                                </ListItemIcon>
                                <ListItemText
                                    primary={item.text}
                                    primaryTypographyProps={{
                                        fontSize: '0.9375rem',
                                        fontWeight: isActive ? 600 : 400,
                                    }}
                                />
                                {isActive && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            left: 0,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            width: 4,
                                            height: 24,
                                            bgcolor: 'primary.main',
                                            borderRadius: '0 4px 4px 0',
                                        }}
                                    />
                                )}
                            </ListItemButton>
                        </ListItem>
                    );
                })}
            </List>

            <Divider sx={{ mx: 2 }} />

            <Box sx={{ p: 2 }}>
                <Box
                    sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                    }}
                >
                    <Box display="flex" alignItems="center" mb={1}>
                        <Circle sx={{ fontSize: 12, color: 'success.main', mr: 1 }} />
                        <Typography variant="caption" fontWeight={600}>
                            Система активна
                        </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        Версия 1.0.0
                    </Typography>
                </Box>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', bgcolor: 'grey.50', minHeight: '100vh' }}>
            <CssBaseline />
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    ml: { sm: `${drawerWidth}px` },
                    bgcolor: 'background.paper',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Toolbar sx={{ py: 1 }}>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { sm: 'none' }, color: 'text.primary' }}
                    >
                        <MenuIcon />
                    </IconButton>

                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" noWrap sx={{ color: 'text.primary', fontWeight: 600 }}>
                            {filteredMenuItems.find(item => item.path === location.pathname)?.text || 'Страница'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {new Date().toLocaleDateString('ru-RU', {
                                weekday: 'long',
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                            })}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title={darkMode ? 'Светлая тема' : 'Темная тема'}>
                            <IconButton onClick={toggleDarkMode} color="inherit">
                                {darkMode ? <Brightness7 sx={{ color: 'text.secondary' }} /> : <Brightness4 sx={{ color: 'text.secondary' }} />}
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Уведомления">
                            <IconButton color="inherit">
                                <Badge badgeContent={3} color="error">
                                    <Notifications sx={{ color: 'text.secondary' }} />
                                </Badge>
                            </IconButton>
                        </Tooltip>

                        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                        <Box display="flex" alignItems="center" gap={1.5}>
                            <Box textAlign="right" display={{ xs: 'none', md: 'block' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    {authStore.user?.username}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {authStore.isAdmin ? 'Администратор' : 'Пользователь'}
                                </Typography>
                            </Box>

                            <IconButton
                                size="large"
                                aria-label="account of current user"
                                aria-controls="menu-appbar"
                                aria-haspopup="true"
                                onClick={handleMenuClick}
                                sx={{ p: 0.5 }}
                            >
                                <Avatar
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        bgcolor: 'primary.main',
                                        fontWeight: 600,
                                    }}
                                >
                                    {getUserInitials()}
                                </Avatar>
                            </IconButton>
                        </Box>

                        <Menu
                            id="menu-appbar"
                            anchorEl={anchorEl}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                            PaperProps={{
                                sx: {
                                    mt: 1,
                                    minWidth: 200,
                                    borderRadius: 2,
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                }
                            }}
                        >
                            <Box sx={{ px: 2, py: 1.5 }}>
                                <Typography variant="subtitle2" fontWeight={600}>
                                    {authStore.user?.username}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {authStore.user?.email}
                                </Typography>
                            </Box>
                            <Divider />
                            <MenuItem onClick={handleLogout} sx={{ py: 1.5 }}>
                                <ListItemIcon>
                                    <Logout fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>Выйти</ListItemText>
                            </MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </AppBar>

            <Box
                component="nav"
                sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
            >
                <Drawer
                    container={window.document.body}
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true,
                    }}
                    sx={{
                        display: { xs: 'block', sm: 'none' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: drawerWidth,
                            bgcolor: 'background.paper',
                            borderRight: '1px solid',
                            borderColor: 'divider',
                        },
                    }}
                >
                    {drawer}
                </Drawer>
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', sm: 'block' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: drawerWidth,
                            bgcolor: 'background.paper',
                            borderRight: '1px solid',
                            borderColor: 'divider',
                        },
                    }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    minHeight: '100vh',
                }}
            >
                <Toolbar />
                <Container maxWidth={false} sx={{ mt: 2, mb: 4 }}>
                    <Outlet />
                </Container>
            </Box>
        </Box>
    );
});

export default Layout;